<script setup lang="ts">
  import useCpBem from '~/composables/useCpBem';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import CpProductCardHome from '~/components/cp/CpProductCard/CpProductCardHome/CpProductCardHome.vue';
  import type {ICpArticle} from '~/types';
  import {usePdp} from '~/stores';
  import type {ICatalogRecommendedParams} from '~/utils/services/api';
  import {useRecommendationsWithArticles} from '~/composables';
  import CpSkeleton from '~/components/cp/skeleton/CpSkeleton/CpSkeleton.vue';
  import {parsePriceInt} from '~/utils';

  const props = defineProps<{
    kind: 'similar' | 'boughtTogether' | 'recommendations'
  }>();

  const store = usePdp();

  const similarItems: ICatalogRecommendedParams = {
    shop: 'cp',
    slot: 'detail2',
    slotName: 'shop_api_detail2_1',
    sku: store.getSku,
  };

  const recommendationsItems: ICatalogRecommendedParams = {
    shop: 'cp',
    slot: 'detail0',
    slotName: 'shop_api_detail0_1',
    sku: store.getSku,
  };

  const productBoughtTogetherItems: ICatalogRecommendedParams = {
      shop: 'cp',
      slot: 'detail1',
      slotName: 'shop_api_detail1_2',
      sku: store.getSku,
  };

  const getCardKind = (item: ICpArticle) => {
    if (item.offer.percent && item.isDailyOffer) {
      return 'FeaturedOffer';
    } else if (item.offer.percent) {
      return 'Offer';
    } else if (item.isDailyOffer) {
      return 'Featured';
    } else if (item.eol) {
      return 'Oos';
    } else if (item.offer.percent && item.eol) {
      return 'OosOffer';
    } else {
      return 'Standard';
    }
  };
  const {b, e} = useCpBem('cp-cross-selling-rai-pdp');

  const objectParams = {
    similar: similarItems,
    boughtTogether: recommendationsItems,
    recommendations: productBoughtTogetherItems,
  };

  const params = objectParams[props.kind];
  const {data, pending} = await useRecommendationsWithArticles(params);
  const title = computed<string>(() => data.value?.title ?? '');
</script>

<template>
    <div v-if="data?.articles?.length" :class="b" data-testid="similar-products-rai">
        <div v-if="pending || !data?.title">
            <div :class="e('card-group')">
                <CpSkeleton v-for="n in 4"
                    :key="n"
                    kind="article-card-vertical-rai"
                    :class="e('product')"
                    effect="fade"
                />
            </div>
        </div>
        <div v-else-if="data && data?.articles.length > 1">
            <CpSectionHeader type="h2" :title="title" :class="e`title`" show-top-border />
            <VLazy :options="{ threshold: 0 }">
                <div :class="e('card-group')" data-testid="rai-card-group">
                    <CpProductCardHome
                        v-for="item in data?.articles"
                        :id="item.id"
                        :key="item.id"
                        :class="e('product')"
                        :kind="getCardKind(item)"
                        :product-type="item.isEsd ? 'esd' : 'physical'"
                        :pill-large="false"
                        pill-short
                        :rate="item.rating.total > 0"
                        :rate-value="item.rating.global"
                        :reviews="item.rating.total"
                        :free-shipping="item.freeShipping"
                        :link="item.link"
                        :title="item.title"
                        :has-discount="item.isOffer || item.tPrice > parsePriceInt(item.price)"
                        :discount="item.offer.percent"
                        :picture="item.picture"
                        :image-title="item.title"
                        :price="item.price"
                        :t-price="item.tPrice"
                    />
                </div>
            </VLazy>
        </div>
    </div>
</template>

<style scoped lang="scss">
.cp-cross-selling-rai-pdp {
  &__card-group {
    display: flex;
    padding: $cp-gutter;
    gap: $cp-gutter;
    overflow: auto;
  }
  &__title {
    width: 360px;
  }
  &__product {
    width: 136px !important;
    height: 300px !important;
  }
  &__card-group::-webkit-scrollbar {
      height: 0;
      display: none;
  }
}
</style>
